import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Stack,
  Grid,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DevicesIcon from '@mui/icons-material/Devices';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../api';

/**
 * QuickStats Component
 * Displays animated summary cards with key metrics
 */
function QuickStats({ deviceId = null }) {
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalUsageTime: 0,
    avgStressLevel: 0,
    activeDevices: 0,
    alerts: 0,
  });
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const { t } = useTranslation();

  const fetchStats = React.useCallback(async () => {
    // Don't fetch if no device is selected
    if (!deviceId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // Fetch activity feed to get session count and calculate stats
      const params = { limit: 100, device_id: deviceId };
      
      const response = await api.get('/api/activity-feed/', { params });
      const activities = response.data.activities || [];
      
      // Calculate stats from activities
      const totalSessions = activities.length;
      const totalUsageTime = activities.reduce((sum, a) => sum + a.duration, 0);
      const avgStress = activities.length > 0
        ? activities.reduce((sum, a) => sum + a.stress_level, 0) / activities.length
        : 0;
      const alerts = activities.filter(a => 
        a.type === 'high_stress_alert' || a.type === 'moderate_stress_alert'
      ).length;
      
      // Get unique devices
      const uniqueDevices = new Set(activities.map(a => a.device_id).filter(Boolean));
      
      setStats({
        totalSessions,
        totalUsageTime,
        avgStressLevel: avgStress,
        activeDevices: uniqueDevices.size,
        alerts,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const StatCard = ({ icon, label, value, subtext, color, trend }) => (
    <Card 
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2,
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box
              sx={{
                bgcolor: `${color}15`,
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {React.cloneElement(icon, { sx: { color, fontSize: 28 } })}
            </Box>
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {trend > 0 ? (
                  <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                ) : (
                  <TrendingDownIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
                )}
              </Box>
            )}
          </Stack>
          
          <Box>
            <Typography 
              variant="h4" 
              fontWeight="bold"
              sx={{ 
                color: theme.palette.text.primary,
                lineHeight: 1.2 
              }}
            >
              {value}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {label}
            </Typography>
            {subtext && (
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {subtext}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          icon={<AccessTimeIcon />}
          label={t('quick_stats.total_sessions', 'Total Sessions')}
          value={stats.totalSessions}
          subtext={t('quick_stats.recent_period', 'Recent period')}
          color={theme.palette.primary.main}
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          icon={<AccessTimeIcon />}
          label={t('quick_stats.total_usage', 'Total Usage Time')}
          value={formatTime(stats.totalUsageTime)}
          subtext={t('quick_stats.combined_time', 'Combined time')}
          color={theme.palette.info.main}
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          icon={<WarningAmberIcon />}
          label={t('quick_stats.avg_stress', 'Avg Stress Level')}
          value={`${stats.avgStressLevel.toFixed(0)}%`}
          subtext={
            stats.avgStressLevel < 33 
              ? t('quick_stats.low', 'Low') 
              : stats.avgStressLevel < 67 
                ? t('quick_stats.moderate', 'Moderate')
                : t('quick_stats.high', 'High')
          }
          color={
            stats.avgStressLevel < 33 
              ? theme.palette.success.main 
              : stats.avgStressLevel < 67 
                ? theme.palette.warning.main
                : theme.palette.error.main
          }
        />
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          icon={<WarningAmberIcon />}
          label={t('quick_stats.alerts', 'Alerts')}
          value={stats.alerts}
          subtext={t('quick_stats.stress_alerts', 'Stress alerts')}
          color={stats.alerts > 5 ? theme.palette.error.main : theme.palette.warning.main}
        />
      </Grid>
    </Grid>
  );
}

export default QuickStats;
